import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../api/axios';
import { FiShoppingCart, FiDollarSign, FiPackage, FiTag } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  confirmed: '#3b82f6',
  preparing: '#8b5cf6',
  ready: '#06b6d4',
  out_for_delivery: '#f97316',
  delivered: '#10b981',
  cancelled: '#ef4444',
};

export default function DashboardHome() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    if (store?.id) {
      api.get(`/stores/${store.id}/dashboard`).then((res) => setStats(res.data));
    }
  }, [store?.id]);

  if (!stats) return <div className="empty-state"><p>{dt('loading', lang)}</p></div>;

  const iconMargin = lang === 'ar' ? { marginLeft: 6 } : { marginRight: 6 };

  const statusData = stats.orders_by_status
    ? Object.entries(stats.orders_by_status).map(([status, count]) => ({
        name: dt(status, lang),
        value: count as number,
        color: STATUS_COLORS[status] || '#94a3b8',
      }))
    : [];

  const topProducts = (stats.top_products || []).map((p: any) => ({
    name: p.product_name?.length > 12 ? p.product_name.slice(0, 12) + '…' : p.product_name,
    qty: p.total_qty,
    revenue: parseFloat(p.total_revenue),
  }));

  return (
    <div>
      <div className="page-header">
        <h1>{dt('dashboard', lang)}</h1>
      </div>

      {/* Stat cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label"><FiShoppingCart style={iconMargin} /> {dt('todays_orders', lang)}</div>
          <div className="stat-value">{stats.today_orders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><FiDollarSign style={iconMargin} /> {dt('todays_revenue', lang)}</div>
          <div className="stat-value">{store?.currency_symbol} {Number(stats.today_revenue).toFixed(3)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><FiShoppingCart style={iconMargin} /> {dt('total_orders', lang)}</div>
          <div className="stat-value">{stats.total_orders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><FiDollarSign style={iconMargin} /> {dt('total_revenue', lang)}</div>
          <div className="stat-value">{store?.currency_symbol} {Number(stats.total_revenue).toFixed(3)}</div>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label"><FiPackage style={iconMargin} /> {dt('total_products', lang)}</div>
          <div className="stat-value">{stats.total_products}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><FiTag style={iconMargin} /> {dt('total_categories', lang)}</div>
          <div className="stat-value">{stats.total_categories}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><FiShoppingCart style={iconMargin} /> {dt('pending_orders', lang)}</div>
          <div className="stat-value" style={{ color: stats.pending_orders > 0 ? 'var(--warning)' : undefined }}>
            {stats.pending_orders}
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="design-grid" style={{ marginTop: 20 }}>
        {/* Revenue chart */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>
            {lang === 'ar' ? 'الإيرادات - آخر 7 أيام' : 'Revenue - Last 7 Days'}
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={stats.daily_revenue || []}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: any) => [`${store.currency_symbol} ${Number(value).toFixed(3)}`, lang === 'ar' ? 'الإيرادات' : 'Revenue']}
                labelFormatter={(label: any) => label}
              />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders chart */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>
            {lang === 'ar' ? 'الطلبات - آخر 7 أيام' : 'Orders - Last 7 Days'}
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.daily_revenue || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                formatter={(value: any) => [value, lang === 'ar' ? 'الطلبات' : 'Orders']}
              />
              <Bar dataKey="orders" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom row: status pie + top products */}
      <div className="design-grid" style={{ marginTop: 20 }}>
        {/* Orders by status */}
        {statusData.length > 0 && (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>
              {lang === 'ar' ? 'الطلبات حسب الحالة' : 'Orders by Status'}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusData.map((entry: any, index: number) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any, name: any) => [value, name]} />
                <Legend
                  formatter={(value: string) => <span style={{ fontSize: 12 }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top products */}
        {topProducts.length > 0 && (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>
              {lang === 'ar' ? 'أكثر المنتجات مبيعاً' : 'Top Products'}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topProducts} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#555' }} width={120} />
                <Tooltip
                  formatter={(value: any, name: any) => [
                    name === 'qty' ? value : `${store.currency_symbol} ${Number(value).toFixed(3)}`,
                    name === 'qty' ? (lang === 'ar' ? 'الكمية' : 'Qty') : (lang === 'ar' ? 'الإيرادات' : 'Revenue'),
                  ]}
                />
                <Bar dataKey="qty" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent orders table */}
      {stats.recent_orders?.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <h2>{dt('recent_orders', lang)}</h2>
          </div>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{dt('order', lang)}</th>
                  <th>{dt('customer', lang)}</th>
                  <th>{dt('total', lang)}</th>
                  <th>{dt('status', lang)}</th>
                  <th>{dt('date', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_orders.map((order: any) => (
                  <tr key={order.id}>
                    <td><strong>{order.order_number}</strong></td>
                    <td>{order.customer_name}</td>
                    <td>{store?.currency_symbol} {Number(order.total).toFixed(3)}</td>
                    <td><span className={`badge badge-${order.status}`}>{dt(order.status, lang)}</span></td>
                    <td>{new Date(order.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
